import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import * as queries from '../../queries';
import { useParams } from 'react-router-dom';
import AuctionNav from './AuctionNav';
import "flatpickr/dist/flatpickr.min.css";
import flatpickr from "flatpickr";
import Swal from 'sweetalert2';
import '../../CSS/General.css';

const AuctionDetails = () => {
    const { t } = useTranslation();
    const { auctionID } = useParams();

    const { loading, error, data, refetch } = useQuery(queries.GET_AUCTIONBYID, {
        variables: {
            AuctionID: auctionID
        },
    });

    const [upsertAuctioninfo] = useMutation(queries.NEW_AUCTIONINFO);
    const [upsertTerms] = useMutation(queries.NEW_TERMS);
    const [upsertAuction] = useMutation(queries.NEW_AUCTION);

    // Initialize state for form fields with default values
    const [formData, setFormData] = useState({
        buyerPremium: '',
        handlingFee: '',
        holdingDays: '',
        finalPickupDate: '',
        paymentDeadline: '',
        taxRate: '',
        warrantyDeadline: '',
        description: '',
        auctionName: '',
        auctionID: '',
        BidPerIncreSecond: '',
        ItemLiveSecond: '',
        termsAndConditions: '',
        redirectLink: '',
        openingTime: '',
        closedTime: '',
        platform: 'Hibid',
        auctionStatus: '',
        auctionNotice: '',
        biddingNotice: ''
    });

    const [initialFormData, setInitialFormData] = useState({});
    const [isExpanded, setIsExpanded] = useState(false);

    const handleDoubleClick = () => {
        setIsExpanded(!isExpanded);
    };

    useEffect(() => {
        if (data && data.oneAuctions) {
            const info = data.oneAuctions;
            const initialData = {
                buyerPremium: info.BuyerPremium || '',
                handlingFee: info.HandlingFee || '',
                holdingDays: info.HoldingDays || '',
                paymentDeadline: info.PaymentDeadline || '',
                taxRate: info.TaxRate || '',
                warrantyDeadline: info.WarrantyDDL || '',
                finalPickupDate: info.FinalPickupDate || '',
                description: info.AuctionDescription || '',
                auctionName: info.AuctionName || '',
                auctionID: info.AuctionID || '',
                BidPerIncreSecond: info.BidPerIncreSecond || '',
                ItemLiveSecond: info.ItemLiveSecond || '',
                termsAndConditions: info.TermsAndCondition || '',
                redirectLink: info.RedirectLink || '',
                openingTime: info.OpeningTime || '',
                closedTime: info.ClosedTime || '',
                platform: info.Platform || '',
                auctionStatus: info.AuctionStatus,
                auctionNotice: info.AuctionNotice || '',
                biddingNotice: info.BiddingNotice || ''
            };
            setFormData(initialData);
            setInitialFormData(initialData);
        }
    }, [data]);

    useEffect(() => {
        flatpickr("#newAuctionStartDate", {
            enableTime: true,
            dateFormat: "Y-m-d H:i",
            altInput: true,
            altFormat: "Y-m-d H:i:S",
            defaultDate: formData.openingTime ? new Date(formData.openingTime) : new Date(),
            onChange: (selectedDates) => {
                setFormData((prevData) => ({
                    ...prevData,
                    openingTime: selectedDates[0].toISOString()
                }));
            }
        });

        flatpickr("#newAuctionEndDate", {
            enableTime: true,
            dateFormat: "Y-m-d H:i",
            altInput: true,
            altFormat: "Y-m-d H:i:S",
            defaultDate: formData.closedTime ? new Date(formData.closedTime) : new Date(),
            onChange: (selectedDates) => {
                setFormData((prevData) => ({
                    ...prevData,
                    closedTime: selectedDates[0].toISOString()
                }));
            }
        });

        flatpickr("#finalPickUpEndDate", {
            enableTime: false,
            dateFormat: "Y-m-d",
            altInput: true,
            altFormat: "Y-m-d",
            defaultDate: formData.finalPickupDate ? formData.finalPickupDate : null,
            onChange: (selectedDates) => {
                setFormData((prevData) => ({
                    ...prevData,
                    finalPickupDate: selectedDates[0].toISOString().split('T')[0]
                }));
            }
        });

    }, [formData.openingTime, formData.closedTime, formData.finalPickupDate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleSave = async () => {
        const auctionInfo = data.oneAuctions.AuctionInfoID;
        let updatedTermID = data.oneAuctions.TermCondition;
        let updatedAuctionInfoID = data.oneAuctions.AuctionInfoID;

        const {
            termsAndConditions,
            buyerPremium,
            handlingFee,
            holdingDays,
            paymentDeadline,
            taxRate,
            warrantyDeadline,
            finalPickupDate,
            description,
            auctionID,
            auctionName,
            redirectLink,
            platform,
            auctionNotice,
            biddingNotice,
            auctionStatus
        } = formData;

        const {
            termsAndConditions: initialTermsAndConditions,
            buyerPremium: initialBuyerPremium,
            handlingFee: initialHandlingFee,
            holdingDays: initialHoldingDays,
            paymentDeadline: initialPaymentDeadline,
            taxRate: initialTaxRate,
            warrantyDeadline: initialWarrantyDeadline,
            finalPickupDate: initialFinalPickupDate,
            auctionNotice: initialAuctionNotice,
            biddingNotice: initialBiddingNotice
        } = initialFormData;

        try {
            updatedTermID = await updateTermsIfNeeded(termsAndConditions, initialTermsAndConditions, updatedTermID);
            updatedAuctionInfoID = await updateAuctionInfoIfNeeded({
                buyerPremium,
                handlingFee,
                holdingDays,
                paymentDeadline,
                taxRate,
                warrantyDeadline,
                auctionNotice,
                biddingNotice,
                initialBuyerPremium,
                initialHandlingFee,
                initialHoldingDays,
                initialPaymentDeadline,
                initialTaxRate,
                initialWarrantyDeadline,
                initialAuctionNotice,
                initialBiddingNotice,
                updatedTermID,
                auctionInfo
            });

            await upsertAuction({
                variables: {
                    AuctionDescription: description,
                    AuctionID: auctionID,
                    AuctionInfoID: updatedAuctionInfoID,
                    AuctionName: auctionName,
                    RedirectLink: redirectLink,
                    FinalPickupDate: finalPickupDate,
                    Platform: platform,
                    ClosedTime: new Date(document.getElementById('newAuctionEndDate').value),
                    OpeningTime: new Date(document.getElementById('newAuctionStartDate').value),
                    AuctionStatus: parseInt(auctionStatus),
                    BidPerIncreSecond: parseInt(formData.BidPerIncreSecond),
                    ItemLiveSecond: parseInt(formData.ItemLiveSecond),
                }
            });

            Swal.fire({ title: t("Updated Successfully") });
            refetch();
        } catch (error) {
            console.error('Mutation error:', error);
            if (error.networkError) {
                console.error('Network error:', error.networkError);
            }
            if (error.graphQLErrors) {
                error.graphQLErrors.forEach(({ message, locations, path }) => {
                    console.error(`GraphQL error: Message: ${message}, Location: ${locations}, Path: ${path}`);
                });
            }
        }
    };

    const updateTermsIfNeeded = async (termsAndConditions, initialTermsAndConditions, updatedTermID) => {
        if (termsAndConditions !== initialTermsAndConditions) {
            const response = await upsertTerms({
                variables: { TermsAndCondition: termsAndConditions }
            });
            updatedTermID = response.data.upsertAuctiontermcondition.itemAfterUpsert.AuctionTermConditionID;
        }
        return updatedTermID;
    };

    const updateAuctionInfoIfNeeded = async ({
        buyerPremium,
        handlingFee,
        holdingDays,
        paymentDeadline,
        taxRate,
        warrantyDeadline,
        auctionNotice,
        biddingNotice,
        initialBuyerPremium,
        initialHandlingFee,
        initialHoldingDays,
        initialPaymentDeadline,
        initialTaxRate,
        initialWarrantyDeadline,
        initialAuctionNotice,
        initialBiddingNotice,
        updatedTermID,
        auctionInfo
    }) => {
        if (
            buyerPremium !== initialBuyerPremium ||
            handlingFee !== initialHandlingFee ||
            holdingDays !== initialHoldingDays ||
            paymentDeadline !== initialPaymentDeadline ||
            taxRate !== initialTaxRate ||
            warrantyDeadline !== initialWarrantyDeadline ||
            auctionNotice !== initialAuctionNotice ||
            biddingNotice !== initialBiddingNotice
        ) {
            const response = await upsertAuctioninfo({
                variables: {
                    BuyerPremium: parseFloat(buyerPremium),
                    HandlingFee: parseFloat(handlingFee),
                    HoldingDays: parseInt(holdingDays, 10),
                    LocationCode: data.oneAuctions.LocationCode,
                    PaymentDeadline: parseInt(paymentDeadline, 10),
                    TaxRate: parseFloat(taxRate),
                    TermCondition: updatedTermID,
                    WarrantyDDL: parseInt(warrantyDeadline, 10),
                    AuctionNotice: auctionNotice,
                    BiddingNotice: biddingNotice
                }
            });
            return response.data.upsertAuctioninfo.itemAfterUpsert.AuctionInfoID;
        }
        return auctionInfo.AuctionInfoID;
    };

    if (loading) return <p>{t('Loading...')}</p>;
    if (error) return <p>{t('Error:')}{error.message}</p>;
    return (
        (<div className='main-container'>
            <AuctionNav auctionID={auctionID} />
            <h1>{t('Auction Details')}</h1>
            <div>
                <form className='new-auction'>
                    <div className='input-info-part requiredFields'>
                        <label htmlFor="platform">{t('Platform')}</label>
                        <select name="platform" value={formData.platform} onChange={handleInputChange}>
                            <option value="Hibid">{t('Hibid')}</option>
                            <option value="Official">{t('Official')}</option>
                        </select>
                    </div>
                    <div className='input-info-part requiredFields'>
                        <label htmlFor="auctionID">{t('Auction ID')}</label>
                        <input type="text" name="auctionID" value={formData.auctionID} onChange={handleInputChange} placeholder={t("Auction ID")} />
                    </div>

                    <div className='input-info-part requiredFields'>
                        <label htmlFor="auctionName">{t('Auction Name')}</label>
                        <input type="text" name="auctionName" value={formData.auctionName} onChange={handleInputChange} placeholder={t("Auction Name")} />
                    </div>

                    <div className='input-info-part requiredFields'>
                        <label htmlFor="redirectLink">{t('Redirect Link')}</label>
                        <input type="text" name="redirectLink" value={formData.redirectLink} onChange={handleInputChange} placeholder={t("Redirect Link")} />
                    </div>
                    <div className='input-info-part requiredFields'>
                        <label htmlFor="openingTime">{t('Opening Time')}</label>
                        <input id="newAuctionStartDate" type="text" name="openingTime" value={formData.openingTime} onChange={handleInputChange} placeholder={t("Opening Time")} readOnly />
                    </div>
                    <div className='input-info-part requiredFields'>
                        <label htmlFor="openingTime">{t('Closing Time')}</label>
                        <input id="newAuctionEndDate" type="text" name="closedTime" value={formData.closedTime} onChange={handleInputChange} placeholder={t("Closed Time")} readOnly />
                    </div>
                    <div className='input-info-part requiredFields'>
                        <label htmlFor="BidPerIncreSecond">{t('Soft Close Seconds')}</label>
                        <input type="text" name="BidPerIncreSecond" value={formData.BidPerIncreSecond} onChange={handleInputChange} placeholder={t("Soft Close Seconds")} />
                    </div>
                    <div className='input-info-part requiredFields'>
                        <label htmlFor="ItemLiveSecond">{t('Item Live Seconds')}</label>
                        <input type="text" name="ItemLiveSecond" value={formData.ItemLiveSecond} onChange={handleInputChange} placeholder={t("Item Live Seconds")} />
                    </div>
                    <div className='input-info-part requiredFields'>
                        <label htmlFor="buyerPremium">{t('Buyer Premium%')}</label>
                        <input type="text" name="buyerPremium" value={formData.buyerPremium} onChange={handleInputChange} placeholder={t("Buyer Premium")} />
                    </div>
                    <div className='input-info-part requiredFields'>
                        <label htmlFor="handlingFee">{t('Handling Fee')}</label>
                        <input type="text" name="handlingFee" value={formData.handlingFee} onChange={handleInputChange} placeholder={t("Handling Fee")} />
                    </div>
                    <div className='input-info-part requiredFields'>
                        <label htmlFor="holdingDays">{t('Holding Days')}</label>
                        <input type="text" name="holdingDays" value={formData.holdingDays} onChange={handleInputChange} placeholder={t("Holding Days")} />
                    </div>
                    <div className='input-info-part requiredFields'>
                        <label htmlFor="paymentDeadline">{t('Payment Deadline (days)')}</label>
                        <input type="text" name="paymentDeadline" value={formData.paymentDeadline} onChange={handleInputChange} placeholder={t("Payment Deadline")} />
                    </div>
                    <div className='input-info-part requiredFields'>
                        <label htmlFor="taxRate">{t('Tax Rate')}</label>
                        <input type="text" name="taxRate" value={formData.taxRate} onChange={handleInputChange} placeholder={t("Tax Rate")} />
                    </div>
                    <div className='input-info-part requiredFields'>
                        <label htmlFor="warrantyDeadline">{t('Warranty Deadline')}</label>
                        <input type="text" name="warrantyDeadline" value={formData.warrantyDeadline} onChange={handleInputChange} placeholder={t("Warranty Deadline")} />
                    </div>
                    <div className='input-info-part requiredFields'>
                        <label htmlFor="auctionStatus">{t('Auction Status')}</label>
                        <select name="auctionStatus" value={formData.auctionStatus} onChange={handleInputChange}>
                            <option value="0">{t('Closed Auctions')}</option>
                            <option value="1">{t('Current Auctions')}</option>
                            <option value="2">{t('Paused Auctions')}</option>
                            <option value="3">{t('Upcoming Auction')}</option>
                        </select>
                    </div>
                    <div className='input-info-part'>
                        <label htmlFor="FinalPickupDate">{t('Final Pickup Date')}</label>
                        <input id="finalPickUpEndDate" type="text" name="FinalPickupDate" value={formData.finalPickupDate} onChange={handleInputChange} placeholder={t("YYYY-MM-DD")} readOnly />
                    </div>

                    <div className=' input-info-part requiredFields textarea-input-part'>
                        <label htmlFor="auctionNotice">{t('Auction Notice')}</label>
                        <textarea name="auctionNotice" value={formData.auctionNotice} onChange={handleInputChange} placeholder={t("Auction Notice")} />
                    </div>

                    <div className=' input-info-part requiredFields textarea-input-part'>
                        <label htmlFor="biddingNotice">{t('Bidding Notice')}</label>
                        <textarea name="biddingNotice" value={formData.biddingNotice} onChange={handleInputChange} placeholder={t("Bidding Notice")} />
                    </div>

                    <div className='input-info-part requiredFields textarea-input-part'>
                        <label htmlFor="description">{t('Description')}</label>
                        <textarea name="description" value={formData.description} onChange={handleInputChange} placeholder={t("Description")} />
                    </div>

                    <div className='input-info-part requiredFields textarea-input-part'>
                        <label htmlFor="termsAndConditions">{t('Terms And Condition')}</label>
                        <textarea
                            name="termsAndConditions"
                            value={formData.termsAndConditions}
                            onChange={handleInputChange}
                            onDoubleClick={handleDoubleClick}
                            placeholder={t("Terms and Conditions")}
                            className={isExpanded ? 'TermsAndCondition TermsAndCondition-expanded' : 'TermsAndCondition'}
                        />
                    </div>
                    <button type="button" onClick={handleSave}>{t('Save')}</button>
                </form>
            </div >
        </div>)
    );
};

export default AuctionDetails;